/* Virtual Try-On on product cards.
 *
 * One shared VTON instance per page (snippets/vton-shared.liquid, rendered
 * once from layout/theme.liquid). Every trigger - the product-page CTA and
 * every card button - is a [data-vton-card-open] element; this file re-points
 * the shared instance at the right GLB and opens it.
 *
 * Two deliberate choices:
 *
 * 1. The card buttons are injected from here rather than from Liquid.
 *    snippets/product-card.liquid is 32 KB and is shared by the collection
 *    grid, the homepage carousels and search. Editing it by hand to add six
 *    lines risks the whole grid; a DOM pass costs nothing and is trivially
 *    reversible by deleting one script tag. Every storefront product is tagged
 *    'vton' and has a GLB (checked: 34/34), so there is nothing to gate on.
 *
 * 2. The GLB URL is resolved on CLICK, not at render. Cards do not carry it,
 *    and fetching it for twelve cards on page load would add twelve requests
 *    to pages that are already slow. /products/<handle>.js is small, cached by
 *    the browser, and only ever fetched for a frame somebody actually taps.
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-vton-root][data-vton-shared]';
  var MAX_TRIES = 20;          // 20 x 100ms = 2s, then fall back to the PDP
  var ICON =
    '<svg class="vton-card__cta-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="7" cy="12" r="4" stroke="currentColor" stroke-width="1.3"/>' +
    '<circle cx="17" cy="12" r="4" stroke="currentColor" stroke-width="1.3"/>' +
    '<path d="M11 12h2M1 10h2M21 10h2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>' +
    '</svg>';

  function sharedInstance() {
    var root = document.querySelector(ROOT_SEL);
    return root && root.__vton ? root.__vton : null;
  }

  /* ---- button injection -------------------------------------------------- */

  /* The card's product link is not inside .card-type__hover, it is a sibling
     further up (.card-information__wrapper on this theme). Walk outward rather
     than hard-coding that class, so a theme update that renames one wrapper
     does not silently drop every button. */
  function productHref(hover) {
    var n = hover.parentElement;
    for (var i = 0; i < 8 && n; i++) {
      var a = n.querySelector && n.querySelector('a[href*="/products/"]');
      if (a) return a.getAttribute('href');
      n = n.parentElement;
    }
    return null;
  }

  function title(hover) {
    var n = hover.parentElement;
    for (var i = 0; i < 8 && n; i++) {
      var t = n.querySelector && n.querySelector('.card__title');
      if (t && t.textContent.trim()) return t.textContent.trim();
      n = n.parentElement;
    }
    return 'this frame';
  }

  function inject(scope) {
    var hovers = (scope || document).querySelectorAll('.card-type__hover');
    for (var i = 0; i < hovers.length; i++) {
      var h = hovers[i];
      if (h.getAttribute('data-vton-wired')) continue;
      var href = productHref(h);
      if (!href) continue;
      h.setAttribute('data-vton-wired', '1');

      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vton-card__cta';
      b.setAttribute('data-vton-card-open', '');
      b.setAttribute('data-vton-href', href);
      b.setAttribute('data-vton-title', title(h));
      b.setAttribute('aria-haspopup', 'dialog');
      b.setAttribute('aria-label', 'Virtual try-on: ' + title(h));
      b.innerHTML = ICON + '<span><span class="vton-card__cta-long">Virtual </span>Try-On</span>';
      h.appendChild(b);
    }
  }

  /* ---- model swapping ---------------------------------------------------- */

  /* vton-engine.js keeps the renderer, scene and FaceLandmarker alive for the
     life of the page: close() leaves sceneReady true, and start() skips the
     whole init chain when it is. Without this the second frame opened would
     still show the first frame's GLB. loadModel() reads this.modelUrl at call
     time and clears frameGroup first, so re-calling it is all that is needed.
     It is hung off start() rather than the card click so the 1.5-3 MB GLB is
     only fetched once the shopper has committed to the camera. */
  function patch(inst) {
    if (inst.__vtonCardsPatched) return;
    var proto = Object.getPrototypeOf(inst);
    if (!proto || typeof proto.start !== 'function' || typeof proto.loadModel !== 'function') return;

    var protoStart = proto.start, protoLoadModel = proto.loadModel;

    inst.loadModel = function () {
      var self = this, url = this.modelUrl;
      return protoLoadModel.call(this).then(function (r) {
        self.__vtonLoadedUrl = url;
        return r;
      });
    };

    inst.start = function () {
      if (this.renderer && this.sceneReady && this.__vtonLoadedUrl !== this.modelUrl) {
        var self = this;
        this.sceneReady = false;
        this.initPromise = this.loadModel().then(function () {
          self.sceneReady = true;
        }).catch(function (e) {
          self.initPromise = null;      // next attempt re-runs the full chain
          throw e;
        });
      }
      return protoStart.apply(this, arguments);
    };

    inst.__vtonCardsPatched = true;
  }

  var glbCache = {};

  function glbFor(href) {
    if (glbCache[href]) return glbCache[href];
    glbCache[href] = fetch(href.split('?')[0] + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.media) return null;
        for (var i = 0; i < p.media.length; i++) {
          var m = p.media[i];
          if (m.media_type !== 'model' || !m.sources) continue;
          for (var j = 0; j < m.sources.length; j++) {
            if (m.sources[j].format === 'glb') return m.sources[j].url;
          }
        }
        return null;
      })
      .catch(function () { return null; });
    return glbCache[href];
  }

  function openWith(inst, url, name, root) {
    patch(inst);
    if (inst.modal && !inst.modal.hidden) inst.close();
    inst.modelUrl = url;
    inst.productName = name;
    // vton-track.js reads data-product-title off the root at event time.
    if (root) {
      root.setAttribute('data-model-url', url);
      root.setAttribute('data-product-title', name);
    }
    inst.open();
  }

  function openFor(btn, tries) {
    var inst = sharedInstance();
    var href = btn.getAttribute('data-vton-href');

    if (!inst) {
      // vton-engine.js is deferred and may not have run yet.
      if (tries < MAX_TRIES) {
        setTimeout(function () { openFor(btn, tries + 1); }, 100);
        return;
      }
      if (href) window.location.href = href;   // graceful degradation
      return;
    }

    var name = btn.getAttribute('data-vton-title') || 'this frame';
    var known = btn.getAttribute('data-vton-model');

    if (known) { openWith(inst, known, name, inst.root); return; }

    btn.setAttribute('aria-busy', 'true');
    glbFor(href).then(function (url) {
      btn.removeAttribute('aria-busy');
      // No model for this product: send them to the page rather than open an
      // empty try-on.
      if (!url) { if (href) window.location.href = href; return; }
      btn.setAttribute('data-vton-model', url);
      openWith(inst, url, name, inst.root);
    });
  }

  /* Capture phase: ahead of the theme's own card handlers and of
     .product_card_product-link, the absolutely positioned full-card anchor. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-vton-card-open]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    openFor(btn, 0);
  }, true);

  function boot() {
    inject(document);
    // The collection grid loads more cards on scroll, and the homepage
    // carousels build their slides after this file runs.
    try {
      new MutationObserver(function () { inject(document); })
        .observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function () { inject(document); });
})();